import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { autorizzazioniScontoAPI, usersAPI, productsAPI } from '../utils/api';
import { AutorizzazioneSconto, User } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';
import {
  Plus,
  Trash2,
  Loader2,
  ShieldCheck,
  Globe,
  FolderOpen,
  X,
} from 'lucide-react';

export default function DiscountAuthorizations() {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    scope_tipo: 'globale' as 'globale' | 'categoria',
    scope_categoria: '',
    sconto_max: '',
  });

  // Fetch users with role 'commerciale'
  const { data: usersData } = useQuery({
    queryKey: ['users-commerciale'],
    queryFn: async () => {
      const res = await usersAPI.getUsers({ role: 'commerciale' as any, limit: 100, is_active: true });
      return res.data?.data || [];
    },
  });

  const commerciali: User[] = usersData || [];

  // Fetch all authorizations
  const { data: authData, isLoading } = useQuery({
    queryKey: ['autorizzazioni-sconto', selectedUserId],
    queryFn: async () => {
      const params: any = { limit: 100 };
      if (selectedUserId) params.user_id = selectedUserId;
      const res = await autorizzazioniScontoAPI.getAutorizzazioni(params);
      return res.data?.data || [];
    },
  });

  const autorizzazioni: AutorizzazioneSconto[] = authData || [];

  // Fetch categories for dropdown
  const { data: categoriesData } = useQuery({
    queryKey: ['product-categories'],
    queryFn: async () => {
      const res = await productsAPI.getCategories();
      return res.data || [];
    },
  });

  const categories: string[] = categoriesData || [];

  // Group by user
  const byUser = autorizzazioni.reduce<Record<string, AutorizzazioneSconto[]>>((acc, auth) => {
    const uid = auth.user_id;
    if (!acc[uid]) acc[uid] = [];
    acc[uid].push(auth);
    return acc;
  }, {});

  const createMutation = useMutation({
    mutationFn: (data: { user_id: string; scope_tipo: string; scope_categoria?: string; sconto_max: number }) =>
      autorizzazioniScontoAPI.createAutorizzazione(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autorizzazioni-sconto'] });
      toast.success('Autorizzazione creata');
      setShowForm(false);
      resetForm();
    },
    onError: () => toast.error('Errore nella creazione'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => autorizzazioniScontoAPI.deleteAutorizzazione(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autorizzazioni-sconto'] });
      toast.success('Autorizzazione eliminata');
    },
    onError: () => toast.error("Errore nell'eliminazione"),
  });

  const resetForm = () => {
    setFormData({ user_id: '', scope_tipo: 'globale', scope_categoria: '', sconto_max: '' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.user_id || !formData.sconto_max) return;

    createMutation.mutate({
      user_id: formData.user_id,
      scope_tipo: formData.scope_tipo,
      scope_categoria: formData.scope_tipo === 'categoria' ? formData.scope_categoria : undefined,
      sconto_max: parseFloat(formData.sconto_max),
    });
  };

  if (!hasPermission('autorizzazioni:view')) {
    return (
      <div className="text-center py-12">
        <ShieldCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-gray-900">Accesso negato</h2>
        <p className="text-gray-500 mt-2">Solo admin e manager possono gestire le autorizzazioni sconto.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Autorizzazioni Sconto</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configura i limiti di sconto per gli agenti commerciali
          </p>
        </div>
        {hasPermission('autorizzazioni:create') && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuova autorizzazione
          </button>
        )}
      </div>

      {/* Filter by user */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Filtra per agente:</label>
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="block w-64 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Tutti gli agenti</option>
            {commerciali.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome} {u.cognome} ({u.email})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Nuova autorizzazione sconto</h2>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Agent selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agente</label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Seleziona agente...</option>
                  {commerciali.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome} {u.cognome}
                    </option>
                  ))}
                </select>
              </div>

              {/* Scope */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ambito</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="scope_tipo"
                      value="globale"
                      checked={formData.scope_tipo === 'globale'}
                      onChange={(e) => setFormData({ ...formData, scope_tipo: e.target.value as 'globale' | 'categoria', scope_categoria: '' })}
                      className="text-primary-600"
                    />
                    <Globe className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">Globale</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="scope_tipo"
                      value="categoria"
                      checked={formData.scope_tipo === 'categoria'}
                      onChange={(e) => setFormData({ ...formData, scope_tipo: e.target.value as 'globale' | 'categoria' })}
                      className="text-primary-600"
                    />
                    <FolderOpen className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">Per categoria</span>
                  </label>
                </div>
              </div>

              {/* Category (conditional) */}
              {formData.scope_tipo === 'categoria' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                  <select
                    value={formData.scope_categoria}
                    onChange={(e) => setFormData({ ...formData, scope_categoria: e.target.value })}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Seleziona categoria...</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Max discount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sconto massimo (%)</label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.sconto_max}
                    onChange={(e) => setFormData({ ...formData, sconto_max: e.target.value })}
                    required
                    min="0.01"
                    max="100"
                    step="0.01"
                    className="block w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Es. 10"
                  />
                  <span className="absolute right-3 top-2 text-gray-400 text-sm">%</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Crea'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : autorizzazioni.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <ShieldCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna autorizzazione configurata</h3>
          <p className="text-gray-500 mb-4">
            Configura i limiti di sconto per gli agenti commerciali.
          </p>
          {hasPermission('autorizzazioni:create') && (
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Aggiungi autorizzazione
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Show grouped by user if no filter, or flat list if filtered */}
          {selectedUserId ? (
            <AuthTable
              autorizzazioni={autorizzazioni}
              onDelete={(id) => deleteMutation.mutate(id)}
              deleting={deleteMutation.isPending}
              canDelete={hasPermission('autorizzazioni:delete')}
            />
          ) : (
            Object.entries(byUser).map(([userId, auths]) => {
              const first = auths[0];
              const userName = [first.user_nome, first.user_cognome].filter(Boolean).join(' ') || first.user_email || userId;
              return (
                <div key={userId} className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-primary-600" />
                      <h3 className="font-semibold text-gray-900">{userName}</h3>
                      <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                        {auths.length} {auths.length === 1 ? 'regola' : 'regole'}
                      </span>
                    </div>
                  </div>
                  <AuthTable
                    autorizzazioni={auths}
                    onDelete={(id) => deleteMutation.mutate(id)}
                    deleting={deleteMutation.isPending}
                    canDelete={hasPermission('autorizzazioni:delete')}
                  />
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// Sub-component for the authorization rules table
function AuthTable({
  autorizzazioni,
  onDelete,
  deleting,
  canDelete,
}: {
  autorizzazioni: AutorizzazioneSconto[];
  onDelete: (id: string) => void;
  deleting: boolean;
  canDelete: boolean;
}) {
  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ambito</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sconto max</th>
          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {autorizzazioni.map((auth) => (
          <tr key={auth.id} className="hover:bg-gray-50">
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                {auth.scope_tipo === 'globale' ? (
                  <>
                    <Globe className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium text-gray-900">Globale</span>
                  </>
                ) : (
                  <>
                    <FolderOpen className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium text-gray-900">Per categoria</span>
                  </>
                )}
              </div>
            </td>
            <td className="px-4 py-3 text-sm text-gray-600">
              {auth.scope_categoria || '—'}
            </td>
            <td className="px-4 py-3 text-right">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                {Number(auth.sconto_max).toFixed(1)}%
              </span>
            </td>
            <td className="px-4 py-3 text-right">
              {canDelete && (
                <button
                  onClick={() => onDelete(auth.id)}
                  disabled={deleting}
                  className="text-red-400 hover:text-red-600 disabled:opacity-50"
                  title="Elimina"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
